import { RepairPriceBreakdown } from "./RepairPricing";
import type { Deal } from "../../lib/proposalDealsApi";
import { money, logoFor, themeStyle } from "../../lib/proposalPresentation";
import { EvidenceImage } from "./EvidenceImage";
import "./dealPresentation.css";

export function DealProposalPreview({ deal }: { deal: Deal }) {
  const insurance = deal.deal_type === "insurance";
  const logo = deal.brand ? logoFor(deal.brand, "light").url : undefined;
  const entries = insurance ? deal.findings : deal.repair_items;
  return (
    <article
      className="deal-presentation"
      data-joy-color-scheme="light"
      style={deal.brand ? themeStyle(deal.brand, "light") : undefined}
    >
      {deal.brand && (
        <link
          rel="stylesheet"
          href={`https://fonts.googleapis.com/css2?${[...new Set([deal.brand.heading_font, deal.brand.body_font])].map((f) => `family=${encodeURIComponent(f)}:wght@400;600;700;800`).join("&")}&display=swap`}
        />
      )}
      <header className="deal-presentation-masthead">
        {logo ? (
          <img src={logo} alt={deal.organization_name || "Company logo"} />
        ) : (
          <strong>{deal.organization_name}</strong>
        )}
        <span>
          {deal.proposal_number}
          <small>Prepared for {deal.contact_name}</small>
        </span>
      </header>
      <div className="deal-presentation-hero">
        <span className="deal-presentation-eyebrow">
          {insurance ? "Your property inspection" : "Your repair proposal"}
        </span>
        <h1>
          {insurance
            ? "Your property. Every detail documented."
            : "A clear plan for your repairs."}
        </h1>
        <p>{deal.title}</p>
      </div>
      <div className="deal-presentation-intro">
        <div>
          <span className="deal-presentation-eyebrow">Prepared for</span>
          <h2>{deal.contact_name}</h2>
          <p>{deal.property_address || deal.opportunity_name}</p>
        </div>
        {!insurance && (
          <aside>
            <span className="deal-presentation-eyebrow">Repair investment</span>
            <strong>{money(deal.totals?.total)}</strong>
            <p>Defined scope, transparent pricing and clear terms.</p>
          </aside>
        )}
      </div>
      {entries.map((item, index) => (
        <section className="deal-presentation-scope" key={item.id}>
          <span className="deal-presentation-eyebrow">
            {insurance ? "Finding" : "Repair"}{" "}
            {String(index + 1).padStart(2, "0")}
          </span>
          <h2>{item.title}</h2>
          <p className="deal-presentation-location">
            {item.location}
            {"system" in item && item.system ? ` · ${item.system}` : ""}
          </p>
          {"issue" in item && item.issue && (
            <>
              <h3>What we found</h3>
              <p>{item.issue}</p>
            </>
          )}
          {"solution" in item ? (
            <>
              <h3>Recommended work</h3>
              <p>{item.solution}</p>
              <div className="deal-presentation-price">
                {item.quantity} {item.unit}
                <strong>
                  {Number(item.discount_amount) > 0 && (
                    <>
                      <s>{money(item.gross_amount)}</s>
                      <small> Save {money(item.discount_amount)} </small>
                    </>
                  )}
                  {money(
                    item.line_total ??
                      Number(item.quantity) * Number(item.unit_price),
                  )}
                </strong>
              </div>
            </>
          ) : (
            <p>{item.notes || item.exception_reason}</p>
          )}
          <div className="deal-presentation-photos">
            {deal.evidence
              .filter((p) =>
                insurance ? p.finding === item.id : p.repair_item === item.id,
              )
              .map((p) => (
                <figure key={p.id}>
                  <EvidenceImage photo={p} />
                  <figcaption>{p.caption}</figcaption>
                </figure>
              ))}
          </div>
        </section>
      ))}
      {deal.exclusions && (
        <section>
          <h2>Exclusions and limitations</h2>
          <p>{deal.exclusions}</p>
        </section>
      )}
      {!insurance && (
        <>
          <section>
            <h2>Investment summary</h2>
            <RepairPriceBreakdown totals={deal.totals} />
            <div className="deal-presentation-total">
              <span>Total repair investment</span>
              <strong>{money(deal.totals?.total)}</strong>
            </div>
          </section>
          <section>
            <h2>Repair terms</h2>
            <p>{deal.terms}</p>
          </section>
        </>
      )}
      {!!deal.supporting_documents?.length && (
        <section>
          <h2>Supporting documents</h2>
          {deal.supporting_documents.map((a) => (
            <p key={a.id}>
              <strong>
                {a.category === "manufacturer"
                  ? "Manufacturer specs & warranty"
                  : "Building codes"}
              </strong>{" "}
              · {a.title}
            </p>
          ))}
          <p>
            The complete uploaded documents are included in the issued report.
          </p>
        </section>
      )}
      <footer>
        Prepared by {deal.organization_name}. Draft preview; issued documents
        preserve the version shared with the customer.
      </footer>
    </article>
  );
}
